import { useCallback, useMemo } from 'react';
import { ApiError, NetworkError } from '@cbhq/data-layer';
import type { WorkspaceMembersResult } from '@cbhq/identity-api-hooks/account-management/fetch';
import {
  useRefreshWorkspaceMembers,
  useWithOptimisticWorkspaceMembersUpdates,
} from '@cbhq/identity-api-hooks/account-management/fetch';
import {
  useAddOrganizationMember,
  useBulkCancelInvites,
  useRemoveOrganizationMember,
  useUpdateOrganizationMemberRole,
} from '@cbhq/identity-api-hooks/account-management/mutation';

import { useOrganization } from '../../contexts/OrganizationContext';
import { logError } from '../../utils/logging';
import { isStepUpProofTokenError } from '../../utils/stepUpProofToken';

import type { MemberRole } from './utils/constants';
import type { LegalEntityRole } from './useMultiLEProductSelection';

type OptimisticData = Readonly<WorkspaceMembersResult | undefined>;
type MutationFn = (currentValue: OptimisticData) => [OptimisticData, Promise<unknown>];

function dedupe(entityIds: string[]): string[] {
  return [...new Set(entityIds)];
}

function throwIfError<T>(response: T): asserts response is Exclude<T, NetworkError | ApiError> {
  if (response instanceof NetworkError) {
    throw new Error('Network error. Please check your connection.', { cause: response });
  }
  if (response instanceof ApiError) {
    throw new Error(response.message, { cause: response });
  }
}

type CancelInviteParams = {
  userId: string | undefined;
  email: string;
  legalEntityIds: string[];
};

// Header account-management reads the step-up proof token from. Not a proto
// field: this service logs query parameters and the two DELETE routes have no
// body mapping, so the token would be read from the query string and written to
// our own logs.
const SECOND_FACTOR_PROOF_TOKEN_HEADER = 'second-factor-proof-token';

// Generated mutation hooks take `RequestInit` as their second argument and
// instant-api-hooks-core merges caller headers last, so the proof token needs no
// codegen and no generated request field.
function stepUpRequestInit(actionProofToken?: string): RequestInit | undefined {
  return actionProofToken
    ? { headers: { [SECOND_FACTOR_PROOF_TOKEN_HEADER]: actionProofToken } }
    : undefined;
}

// Janus-minted, single-use proof token for a mutation touching a step-up gated
// role. Never log or persist it.
type ActionProofTokenParam = {
  actionProofToken?: string;
};

type InviteMemberParams = ActionProofTokenParam & {
  email: string;
  roles: MemberRole[];
  legalEntityIds: string[];
  legalEntityRoles?: LegalEntityRole[];
};

// No readOnlyEntityIds here: the single-LE tray gates the whole submission on
// canEditEntity at the component level (EditMember.tsx), so an unauthorized
// entity never reaches this call. Contrast with UpdateMemberRolesPerEntityParams,
// where the multi-LE tray lists entities the user can view but not manage, so
// the mutation itself must exclude them.
type UpdateMemberRolesParams = ActionProofTokenParam & {
  memberUuid: string;
  selectedEntityIds: string[];
  roles: MemberRole[];
};

type UpdateMemberRolesPerEntityParams = ActionProofTokenParam & {
  memberUuid: string;
  legalEntityRoles: LegalEntityRole[];
  /**
   * Entities the current user may see but not manage. Excluded from both
   * legalEntityRoles and removedLegalEntityIds so an unauthorized entity can
   * never fail the whole request.
   */
  readOnlyEntityIds?: string[];
};

type RemoveMemberFromOrgParams = ActionProofTokenParam & {
  memberUuid: string;
};

export function useTeamApi(organizationId: string) {
  const { selectedOrganization } = useOrganization();
  const withOptimisticWorkspaceMembersUpdates = useWithOptimisticWorkspaceMembersUpdates({
    organizationId,
  });
  const refreshWorkspaceMembers = useRefreshWorkspaceMembers({ organizationId });
  const bulkCancelInvites = useBulkCancelInvites();
  const addOrganizationMember = useAddOrganizationMember();
  const removeOrganizationMember = useRemoveOrganizationMember();
  const updateOrganizationMemberRole = useUpdateOrganizationMemberRole();

  const entityIdToName = useMemo(
    () =>
      new Map(
        (selectedOrganization?.legalEntities ?? []).map((le) => [
          le.legalEntityId,
          le.legalEntityName,
        ]),
      ),
    [selectedOrganization?.legalEntities],
  );

  // Wraps withOptimisticWorkspaceMembersUpdates with consistent error handling:
  // refreshes cache and logs on failure, then re-throws for component-level UI handling.
  const runWithOptimisticUpdate = useCallback(
    async (context: string, mutationFn: MutationFn) => {
      try {
        await withOptimisticWorkspaceMembersUpdates(mutationFn);
      } catch (err) {
        void refreshWorkspaceMembers();
        logError(err, { context, isHandled: true, isUserVisible: true });
        throw err;
      }
    },
    [withOptimisticWorkspaceMembersUpdates, refreshWorkspaceMembers],
  );

  const cancelInvite = useCallback(
    async ({ userId, email, legalEntityIds }: CancelInviteParams) => {
      await runWithOptimisticUpdate('TeamApi.cancelInvite', (currentData) => {
        const canceledLegalEntityIds = new Set(legalEntityIds);
        const optimisticData = {
          ...currentData,
          pendingInvites: currentData?.pendingInvites?.filter(
            (invite) =>
              invite.email !== email ||
              invite.legalEntityId == null ||
              !canceledLegalEntityIds.has(invite.legalEntityId),
          ),
        };

        const request = async () => {
          const response = await bulkCancelInvites({ userId, legalEntityIds });
          throwIfError(response);

          // BulkCancelInvites returns HTTP 200 even when an individual invite
          // cancellation fails; the failure is reported per-item in `results`.
          const failedResult = response.results?.find((result) => result.success !== true);
          if (failedResult) {
            throw new Error(failedResult.error || 'Failed to cancel invite');
          }

          return response;
        };

        return [optimisticData, request()];
      });
    },
    [runWithOptimisticUpdate, bulkCancelInvites],
  );

  const inviteMember = useCallback(
    async ({
      email,
      roles,
      legalEntityIds,
      legalEntityRoles,
      actionProofToken,
    }: InviteMemberParams) => {
      await runWithOptimisticUpdate('TeamApi.inviteMember', (currentData) => {
        const legalEntityRolesById = new Map(
          (legalEntityRoles ?? []).map((legalEntityRole) => [
            legalEntityRole.legalEntityId,
            legalEntityRole.roles,
          ]),
        );
        const hasLegalEntityRoles = (legalEntityRoles?.length ?? 0) > 0;
        const optimisticLegalEntityIds = hasLegalEntityRoles
          ? legalEntityRoles?.map((legalEntityRole) => legalEntityRole.legalEntityId) ?? []
          : legalEntityIds;
        const requestLegalEntityIds = hasLegalEntityRoles ? [] : legalEntityIds;

        const optimisticInvites = optimisticLegalEntityIds.map((entityId) => ({
          roles: legalEntityRolesById.get(entityId) ?? roles,
          legalEntityId: entityId,
          createdAt: new Date().toISOString(),
        }));

        const optimisticData = {
          ...currentData,
          pendingInvites: [...(currentData?.pendingInvites ?? []), ...optimisticInvites],
        };

        const request = async () => {
          const response = await addOrganizationMember(
            {
              member: { email, roles },
              legalEntityIds: requestLegalEntityIds,
              legalEntityRoles,
            },
            stepUpRequestInit(actionProofToken),
          );
          throwIfError(response);
          return response;
        };

        return [optimisticData, request()];
      });
    },
    [runWithOptimisticUpdate, addOrganizationMember],
  );

  const updateMemberRoles = useCallback(
    async ({ memberUuid, selectedEntityIds, roles, actionProofToken }: UpdateMemberRolesParams) => {
      await runWithOptimisticUpdate('TeamApi.updateMemberRoles', (currentData) => {
        // Entities where all roles were removed should be treated as deletions, not updates.
        // Calling PUT /role with an empty roles array causes a backend error.
        const emptyRoleEntityIds = roles.length === 0 ? selectedEntityIds : [];
        const emptyRoleSet = new Set(emptyRoleEntityIds);

        const updatedMembers = currentData?.members?.map((m) => {
          if (m.userUuid !== memberUuid) return m;
          const currentLegalEntityRoles = m.legalEntityRoles ?? [];
          const nextLegalEntityRoles = selectedEntityIds
            .filter((entityId) => !emptyRoleSet.has(entityId))
            .map((entityId) => {
              const existingRole = currentLegalEntityRoles.find(
                (ler) => ler.legalEntityId === entityId,
              );
              // Newly-added entities have no existingRole yet, so their optimistic
              // display name comes from the org's legal entity list instead.
              const legalEntityName = existingRole?.legalEntityName ?? entityIdToName.get(entityId);
              return { ...existingRole, legalEntityId: entityId, legalEntityName, roles };
            });
          return { ...m, legalEntityRoles: nextLegalEntityRoles };
        });

        const member = currentData?.members?.find((m) => m.userUuid === memberUuid);
        const memberEntityIds = (member?.legalEntityRoles ?? [])
          .map((ler) => ler.legalEntityId ?? '')
          .filter(Boolean);
        const removedEntityIds = memberEntityIds.filter((id) => !selectedEntityIds.includes(id));
        const currentRolesByEntityId = new Map(
          (member?.legalEntityRoles ?? [])
            .filter((ler) => Boolean(ler.legalEntityId))
            .map((ler) => [ler.legalEntityId ?? '', (ler.roles ?? []) as MemberRole[]]),
        );

        const request = async () => {
          const entitiesToUpdate =
            roles.length === 0
              ? []
              : selectedEntityIds.filter((entityId) => {
                  const currentRoles = currentRolesByEntityId.get(entityId);
                  if (!currentRoles) return true;
                  if (currentRoles.length !== roles.length) return true;
                  const currentRolesSet = new Set(currentRoles);
                  return roles.some((role) => !currentRolesSet.has(role));
                });

          // Deselected entities and entities whose last role was removed are
          // revoked. If the last entity is revoked, the backend removes org
          // membership as a result.
          const updatedEntitySet = new Set(entitiesToUpdate);
          const heldEntityIds = new Set(currentRolesByEntityId.keys());
          const removedLegalEntityIds = dedupe([...removedEntityIds, ...emptyRoleEntityIds]).filter(
            (entityId) => heldEntityIds.has(entityId) && !updatedEntitySet.has(entityId),
          );

          if (entitiesToUpdate.length === 0 && removedLegalEntityIds.length === 0) {
            return;
          }

          try {
            const response = await updateOrganizationMemberRole(
              {
                memberUuid,
                legalEntityRoles: entitiesToUpdate.map((legalEntityId) => ({
                  legalEntityId,
                  roles,
                })),
                removedLegalEntityIds,
              },
              stepUpRequestInit(actionProofToken),
            );
            throwIfError(response);
          } catch (error) {
            // A proof-token rejection has to reach the caller unwrapped, or the
            // prefix it re-prompts on is lost in the generic error below.
            if (isStepUpProofTokenError(error)) {
              throw error;
            }
            throw new Error('Error updating membership', { cause: error });
          }
        };

        return [{ ...currentData, members: updatedMembers }, request()];
      });
    },
    [runWithOptimisticUpdate, updateOrganizationMemberRole, entityIdToName],
  );

  const updateMemberRolesPerEntity = useCallback(
    async ({
      memberUuid,
      legalEntityRoles,
      readOnlyEntityIds,
      actionProofToken,
    }: UpdateMemberRolesPerEntityParams) => {
      await runWithOptimisticUpdate('TeamApi.updateMemberRolesPerEntity', (currentData) => {
        const readOnlySet = new Set(readOnlyEntityIds ?? []);
        const rolesById = new Map(legalEntityRoles.map((ler) => [ler.legalEntityId, ler.roles]));

        const member = currentData?.members?.find((m) => m.userUuid === memberUuid);
        const currentRolesByEntityId = new Map(
          (member?.legalEntityRoles ?? [])
            .filter((ler) => Boolean(ler.legalEntityId))
            .map((ler) => [ler.legalEntityId ?? '', (ler.roles ?? []) as MemberRole[]]),
        );

        // LEs absent from the new list or present with empty roles should be
        // removed. Removal is explicit, so only LEs the member actually holds
        // are named.
        const newEntityIds = new Set(legalEntityRoles.map((ler) => ler.legalEntityId));
        const visibleEntityIds = new Set(
          (selectedOrganization?.legalEntities ?? []).map(({ legalEntityId }) => legalEntityId),
        );
        const delistedEntityIds = [...currentRolesByEntityId.keys()].filter(
          (id) => visibleEntityIds.has(id) && !newEntityIds.has(id) && !readOnlySet.has(id),
        );
        const emptyRoleEntityIds = legalEntityRoles
          .filter(({ roles }) => roles.length === 0)
          .map(({ legalEntityId }) => legalEntityId)
          .filter((id) => !readOnlySet.has(id) && currentRolesByEntityId.has(id));
        const removedEntityIds = dedupe([...delistedEntityIds, ...emptyRoleEntityIds]);

        const updatedMembers = currentData?.members?.map((m) => {
          if (m.userUuid !== memberUuid) return m;
          const removedSet = new Set(removedEntityIds);
          const nextLegalEntityRoles = (m.legalEntityRoles ?? [])
            .filter((ler) => !removedSet.has(ler.legalEntityId ?? ''))
            .map((ler) => {
              const newRoles = rolesById.get(ler.legalEntityId ?? '');
              return newRoles ? { ...ler, roles: newRoles } : ler;
            });
          return { ...m, legalEntityRoles: nextLegalEntityRoles };
        });

        const request = async () => {
          const entitiesToUpdate = legalEntityRoles.filter(({ legalEntityId, roles }) => {
            if (roles.length === 0) return false; // removals handled separately
            if (readOnlySet.has(legalEntityId)) return false;
            const currentRoles = currentRolesByEntityId.get(legalEntityId);
            if (!currentRoles) return true;
            if (currentRoles.length !== roles.length) return true;
            const currentRolesSet = new Set(currentRoles);
            return roles.some((role) => !currentRolesSet.has(role));
          });

          const updatedEntitySet = new Set(entitiesToUpdate.map((ler) => ler.legalEntityId));
          const removedLegalEntityIds = removedEntityIds.filter(
            (entityId) => !updatedEntitySet.has(entityId),
          );

          if (entitiesToUpdate.length === 0 && removedLegalEntityIds.length === 0) {
            // The caller (form) should not invoke this with no actual changes —
            // log so we can spot stale form-validation bugs without surfacing to the user.
            logError(new Error('updateMemberRolesPerEntity called with no changes'), {
              context: 'TeamApi.updateMemberRolesPerEntity',
              isHandled: true,
              isUserVisible: false,
            });
            return;
          }

          try {
            const response = await updateOrganizationMemberRole(
              {
                memberUuid,
                legalEntityRoles: entitiesToUpdate,
                removedLegalEntityIds,
              },
              stepUpRequestInit(actionProofToken),
            );
            throwIfError(response);
          } catch (error) {
            // A proof-token rejection has to reach the caller unwrapped, or the
            // prefix it re-prompts on is lost in the generic error below.
            if (isStepUpProofTokenError(error)) {
              throw error;
            }
            throw new Error('Error updating membership', { cause: error });
          }
        };

        return [{ ...currentData, members: updatedMembers }, request()];
      });
    },
    [runWithOptimisticUpdate, updateOrganizationMemberRole, selectedOrganization?.legalEntities],
  );

  const removeMemberFromOrg = useCallback(
    async ({ memberUuid, actionProofToken }: RemoveMemberFromOrgParams) => {
      await runWithOptimisticUpdate('TeamApi.removeMemberFromOrg', (currentData) => {
        const optimisticData = {
          ...currentData,
          members: currentData?.members?.filter((m) => m.userUuid !== memberUuid),
        };

        const request = async () => {
          const response = await removeOrganizationMember(
            { memberUuid, organizationId },
            stepUpRequestInit(actionProofToken),
          );
          throwIfError(response);
          return response;
        };

        return [optimisticData, request()];
      });
    },
    [runWithOptimisticUpdate, removeOrganizationMember, organizationId],
  );

  return {
    cancelInvite,
    inviteMember,
    updateMemberRoles,
    updateMemberRolesPerEntity,
    removeMemberFromOrg,
  };
}
